import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NgOtpInputModule } from 'ng-otp-input';
import {
  ComponentBase,
  FORGET_PASSWORD_INPUT,
  VERFICATION_RESET_INPUT,
  VERIFY_SIGNUP_INPUT,
  VerifyMobileNumberType,
  SharedService,
  AutoRegistrationService,
  BusinessDetailsService,
} from 'src/app/core';
import { Config as NgOtpInputConfig } from 'ng-otp-input/lib/models/config';
import { ToastrService } from 'ngx-toastr';
import { Subscription, interval } from 'rxjs';
import { AutoRegistrationConfig } from '../../models/auto-registration-config';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { finalize, map, take, takeUntil, tap } from 'rxjs/operators';
import { SalesChatService } from 'src/app/core';
import { CONFIG } from '../../registration-config';
import { VerifyIdentityComponent } from 'src/app/auto-registration/sign-up/verification/verify-identity/verify-identity.component';
import { AutoRegistrationTopBarComponent } from 'src/app/shared/auto-registration-top-bar/auto-registration-top-bar.component';
import { MatDialog } from '@angular/material/dialog';
import { VideoComponent } from 'src/app/shared/video/video.component';

@Component({
  selector: 'app-verification',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NgOtpInputModule,
    VerifyIdentityComponent,
    AutoRegistrationTopBarComponent
  ],
  templateUrl: './verification.component.html',
  styleUrl: './verification.component.scss',
})
export class VerificationComponent extends ComponentBase implements OnInit, OnChanges {
  @Input() isVerifyRoute?: boolean = false;
  /** Registration cycle log ID from parent in inline mode */
  @Input() inlineRegistrationCycleLogId?: string;

  /** Phone number or user identifier from parent in inline mode */
  @Input() inlineUser?: string;

  /** Verify type (SMS or Whatsapp) from parent in inline mode */
  @Input() inlineVerifyType?: VerifyMobileNumberType;

  /** Emitted when verification succeeds to close the sidebar (inline mode) */
  @Output() closeSidebar = new EventEmitter<void>();
  @Output() confirmAndCloseSidebar = new EventEmitter<void>();
  
  /** Registered mobile number displayed to the user */
  public registeredMobileNumber: string = '';

  /** When true, user can request a new verification code (resend) */
  openResendCode: boolean = false;

  /** True after the first successful send action (button becomes resend) */
  hasSentCode: boolean = false;

  /** Locks resend button and verify method cards while countdown is running */
  isCountdownActive: boolean = false;

  /** Channel used for the last successful send; that card stays disabled until countdown ends */
  lastSentVerifyType: VerifyMobileNumberType | null = null;

  /** Payload for resend code (forget password / change verify method) */
  resendCodeData: FORGET_PASSWORD_INPUT = {
    UserName: undefined,
    VerifyMobileNumberType: undefined,
  };

  /** Payload for reset password verification */
  verifyResetData: VERFICATION_RESET_INPUT = {
    UserName: undefined,
    Code: undefined,
  };

  /** Payload for sign-up verification */
  verifySignUpData: VERIFY_SIGNUP_INPUT = {
    RegistrationCycleLogId: undefined,
    VerificationCode: undefined,
  };

  /** Configuration for the OTP input component (length = 4, numeric, auto-focus first field) */
  otpInputConfig: NgOtpInputConfig = {
    length: 4,
    allowNumbersOnly: true,
    disableAutoFocus: true,
  };

  /** Remaining seconds shown on the resend countdown (single timer; not reset when toggling method UI). */
  countdownSecondsRemaining = 0;

  /** Active countdown subscription (unsubscribed on new send or destroy). */
  private countdownSub?: Subscription;

  /** Controls OTP input visibility */
  showOtpInput: boolean = false;

  /** Reference to verify type enum (SMS or Whatsapp) */
  verifyMobileNumberType = VerifyMobileNumberType;

  /** Request flow type: 'auto' (sign-up) or 'normal' (forget password); derived from verifySignUpData */
  get requestType(): string {
    return this.verifySignUpData.RegistrationCycleLogId ? 'auto' : 'normal';
  }

  /** Current OTP code entered by the user */
  otpCode: string = '';

  /** When true, shows the verify identity (change method) component */
  showVerifyIdentity: boolean = false;

  /** When true, confirm button shows loading state */
  confirmLoading: boolean = false;

  /** When true, Send/Resend button shows loading state */
  sendLoading: boolean = false;

  /** Prevents duplicate initial resend when opening verification (auto-send once) */
  private hasTriggeredInitialAutoSend = false;

  /** When true, OTP inputs show red border (set when verify request fails) */
  otpError: boolean = false;

  /** Returns translated verification message with masked phone (last 2 digits) */
  get getVerificationMessage(): string {
    let maskedNumber = '***';
    if (this.registeredMobileNumber && this.registeredMobileNumber.length >= 2) {
      const lastTwoDigits = this.registeredMobileNumber.slice(-2);
      maskedNumber = `***${lastTwoDigits}`;
    }

    if (!this.hasSentCode) {
      const preSendMessage = this.translateService.instant('SELECT_METHOD_THEN_SEND_CODE');
      return preSendMessage.replace('{{number}}', maskedNumber);
    }

    if (this.showAlternateChannelSend) {
      const preSendMessage = this.translateService.instant('SELECT_METHOD_THEN_SEND_CODE');
      return preSendMessage.replace('{{number}}', maskedNumber);
    }

    const isWhatsApp = +this.resendCodeData.VerifyMobileNumberType === 2;
    const messageType = isWhatsApp ? 'Enter_The_Code_WhatsApp' : 'Enter_The_Code_Phone';
    const baseMessage = this.translateService.instant(messageType);
    return baseMessage.replace('{{number}}', maskedNumber);
  }

  /** True while countdown runs and user selected the other channel to send a new code */
  get showAlternateChannelSend(): boolean {
    return (
      this.hasSentCode &&
      this.isCountdownActive &&
      this.lastSentVerifyType != null &&
      this.resendCodeData.VerifyMobileNumberType != null &&
      +this.resendCodeData.VerifyMobileNumberType !== +this.lastSentVerifyType
    );
  }

  /** SMS card locked only when SMS was last sent, countdown runs, and SMS is selected (not while user is on the other channel). */
  get isSmsMethodInCooldown(): boolean {
    const selected = this.resendCodeData.VerifyMobileNumberType;
    return (
      this.isCountdownActive &&
      this.lastSentVerifyType != null &&
      selected != null &&
      +this.lastSentVerifyType === +VerifyMobileNumberType.SMS &&
      +selected === +VerifyMobileNumberType.SMS
    );
  }

  /** Whatsapp card locked only when Whatsapp was last sent, countdown runs, and Whatsapp is selected. */
  get isWhatsappMethodInCooldown(): boolean {
    const selected = this.resendCodeData.VerifyMobileNumberType;
    return (
      this.isCountdownActive &&
      this.lastSentVerifyType != null &&
      selected != null &&
      +this.lastSentVerifyType === +VerifyMobileNumberType.Whatsapp &&
      +selected === +VerifyMobileNumberType.Whatsapp
    );
  }

  /** Send/resend: allowed on first send, after cooldown, or mid-cooldown when alternate channel is selected */
  get canSendCode(): boolean {
    if (!this.resendCodeData.VerifyMobileNumberType || this.sendLoading) {
      return false;
    }
    if (!this.hasSentCode) {
      return true;
    }
    if (!this.isCountdownActive) {
      return true;
    }
    return this.showAlternateChannelSend;
  }

  /** Confirm is enabled only after selecting method and successfully sending code */
  get canConfirmCode(): boolean {
    return !!this.resendCodeData.VerifyMobileNumberType && this.hasSentCode;
  }

  /** Current UI direction (ltr/rtl) from locale */
  dir: string = localStorage.getItem('lang') || '';

  constructor(
    private salesChatService: SalesChatService,
    private readonly autoRegistrationService: AutoRegistrationService,
    private readonly sharedService: SharedService,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly dialog: MatDialog,
    @Inject(CONFIG) public config: AutoRegistrationConfig,
    public readonly translateService: TranslateService,
    readonly businessDetailsService: BusinessDetailsService,
    readonly title: Title,
    private readonly cdr: ChangeDetectorRef
  ) {
    super();

    translateService.onLangChange.subscribe((_) => {
      this.dir = _.lang;
      title.setTitle(translateService.instant('Title_verfication_phone'));
    });
    setTimeout(() => {
      title.setTitle(translateService.instant('Title_verfication_phone'));
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inlineVerifyType']) {
      this.resendCodeData = {
        ...this.resendCodeData,
        VerifyMobileNumberType: this.inlineVerifyType ?? VerifyMobileNumberType.Whatsapp,
      };
    }
  }

  ngOnInit(): void {
    this.salesChatService.chatScript();
    if (this.inlineRegistrationCycleLogId) {
      this.verifySignUpData = {
        RegistrationCycleLogId: this.inlineRegistrationCycleLogId,
      };
      this.sharedService.setCookie('RegistrationCycleLogId', String(this.inlineRegistrationCycleLogId), 30);
    }

    if (this.inlineUser) {
      this.verifyResetData.UserName = this.inlineUser;
      this.resendCodeData.UserName = this.inlineUser;
      this.registeredMobileNumber = this.inlineUser;
    }

    this.resendCodeData = {
      ...this.resendCodeData,
      VerifyMobileNumberType: this.inlineVerifyType ?? VerifyMobileNumberType.Whatsapp,
    };

    const regId = this.sharedService.getCookie('RegistrationCycleLogId');
    if (regId) {
      this.businessDetailsService
        .getRegisteredPhases({
          RegistrationCycleLogId: +regId,
        })
        .pipe(
          map((response) => response?.Result?.One),
          takeUntil(this.destroy$)
        )
        .subscribe((response) => {
          if (response?.OneData?.MobileNumber) {
            this.registeredMobileNumber = response?.OneData?.MobileNumber;
          }
        });
    }

    setTimeout(() => {
      this.showOtpInput = true;
      this.cdr.markForCheck();
    }, 0);

    this.scheduleInitialAutoSend();
  }

  /** Sign-up flow: send verification code once on open (WhatsApp preselected). */
  private scheduleInitialAutoSend(): void {
    setTimeout(() => {
      if (
        this.hasTriggeredInitialAutoSend ||
        this.hasSentCode ||
        this.sendLoading ||
        this.requestType !== 'auto'
      ) return;

      if (!this.resendCodeData.VerifyMobileNumberType) return;
      
      const regId = this.sharedService.getCookie('RegistrationCycleLogId');
      if (!regId) return;
      
      this.hasTriggeredInitialAutoSend = true;
      this.onResendCode();
      this.cdr.markForCheck();
    }, 0);
  }

  /** Handles backToVerification event from verify-identity; resets OTP and resends with new verifyType */
  onBackToVerificationFromIdentity(payload?: { verifyType: any }): void {
    if (payload?.verifyType) {
      this.resendCodeData = {
        ...this.resendCodeData,
        VerifyMobileNumberType: payload.verifyType,
      };
      this.otpCode = '';
      this.cdr.markForCheck();
    }
  }

  /** Hides the verify identity (change method) component */
  onCloseVerifyIdentity(): void {
    this.showVerifyIdentity = false;
  }

  /** Called when user types in OTP input; submits when 4 digits are complete */
  onOtpInputChange(otpCode: string): void {
    this.otpCode = otpCode ?? '';
    this.otpError = false;
    const code = (this.otpCode || '').toString();
    if (
      code.length === 4 &&
      this.canConfirmCode &&
      !this.confirmLoading
    ) {
      this.onConfirm();
    }
  }

  /** Opens the video modal in a dialog */
  onOpenVideo(): void {
    this.dialog.open(VideoComponent, {
      minWidth: '60%',
      maxWidth: '100%',
      panelClass: 'aspect_ratio',
    });
  }

  onCloseTermsSidenav(): void {
    this.closeSidebar.emit();
  }

  /** Called when OTP has 4 digits (from {@link onOtpInputChange}); submits verification */
  onConfirm(): void {
    if (this.otpCode && this.otpCode.length === 4 && !this.confirmLoading) {
      this.confirmLoading = true;
      this.onFill(this.otpCode);
    }
  }

  /** Submits the OTP code: sign-up verification (closeSidebar) or reset password (navigate) */
  onFill(otpCode: string): void {
    if (otpCode?.length === 4) {
      const regId = this.sharedService.getCookie('RegistrationCycleLogId');
      if (!regId) {
        this.confirmLoading = false;
        this.toastr.error(this.translateService.instant('Registration_Session_Expired'));
        this.router.navigate(['registration-cycle']);
        return;
      } else {
        this.verifySignUpData = {
          ...this.verifySignUpData,
          RegistrationCycleLogId: regId,
          VerificationCode: otpCode,
        };

        this.autoRegistrationService
          .verificationPhaseOne(this.verifySignUpData)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.confirmLoading = false))
          )
          .subscribe((res) => {
            this.checkResponse = this.sharedService.checkResponseStatus(
              res.Message,
              res.State
            );
            this.otpError = !this.checkResponse;
            if (this.checkResponse) {
              this.sharedService.playSound('assets/sounds/done.mp3');
              this.confirmAndCloseSidebar.emit();
            };
          });
      }
    }
  }

  /**
   * Countdown 59 → 0 over 60s. One subscription updates {@link countdownSecondsRemaining} so toggling
   * SMS / Whatsapp (hiding the countdown row) does not resubscribe and reset the timer.
   * A new successful {@link onResendCode} restarts from 59.
   */
  private countDown(): void {
    const initialCount = 59;
    if (this.countdownSub) {
      this.countdownSub.unsubscribe();
      this.countdownSub = undefined;
    }

    this.isCountdownActive = true;
    this.openResendCode = false;
    this.countdownSecondsRemaining = initialCount;
    this.cdr.markForCheck();

    this.countdownSub = interval(1000)
      .pipe(
        takeUntil(this.destroy$),
        take(initialCount + 1),
        map((tick) => initialCount - tick),
        tap((remaining) => {
          this.countdownSecondsRemaining = remaining;
          if (remaining === 0) {
            this.isCountdownActive = false;
            this.openResendCode = true;
          }
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        complete: () => {
          this.countdownSub = undefined;
        },
      });
  }

  /** Resends verification code (resendCode for sign-up or forgetPassword for reset) */
  onResendCode(): void {
    if (!this.canSendCode || this.sendLoading) return;
    
    if (this.requestType === 'auto') {
      const regId = this.sharedService.getCookie('RegistrationCycleLogId');
      if (!regId) {
        this.toastr.error(this.translateService.instant('Registration_Session_Expired'));
        this.router.navigate(['registration-cycle']);
        return;
      }
      if (this.resendCodeData.VerifyMobileNumberType) {
        this.sendLoading = true;
        this.autoRegistrationService
          .resendCode({
            VerifyMobileNumberType: this.resendCodeData.VerifyMobileNumberType,
            RegistrationCycleLogId: regId,
          })
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.sendLoading = false))
          )
          .subscribe((res) => {

            if (res.State == 100) {
              this.hasSentCode = true;
              this.lastSentVerifyType =
                this.resendCodeData.VerifyMobileNumberType ?? null;
              this.countDown();
            }

            this.sharedService.checkResponseStatus(res.Message, res.State);

          });
      }
    }

    if (this.requestType === 'normal') {
      if (
        this.resendCodeData.UserName &&
        this.resendCodeData.VerifyMobileNumberType
      ) {
        this.sendLoading = true;
        this.autoRegistrationService
          .forgetPassword(this.resendCodeData)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.sendLoading = false))
          )
          .subscribe((res) => {

            if (res.State == 100) {
              this.hasSentCode = true;
              this.lastSentVerifyType =
                this.resendCodeData.VerifyMobileNumberType ?? null;
              this.countDown();
            }

            this.sharedService.checkResponseStatus(res.Message, res.State);
          });
      } else {
        this.toastr.error('Error Occcurs , please try on another time !');
      }
    }
  }
}
